import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'
import { useParams } from 'react-router-dom'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { CuratorPlaylistsItem } from 'src/Components/CuratorPlaylistsItem'
import { numberToMoney } from 'src/Constants/functions'
import { ListPagination } from 'src/Components/ListPagination'
import { PrimarySkeleton } from 'src/Components/Skeletons/PrimarySkeleton'
import { useContentScroll } from 'src/Hooks/useContentScroll'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import {
  useGetMatchedCuratorsQuery,
  MatchedCurator,
  MatchedCuratorsSortBy,
} from 'src/Redux/matchedCuratorsApi'
import {
  FavoriteCurator,
  useGetFavoriteCuratorsQuery,
} from 'src/Redux/favoriteCuratorsApi'
import { Sorting } from 'src/Components/Buttons/Sorting'
import {
  SortingMatchedCurators,
  MATCHED_CURATORS_SORT_LABELS,
} from 'src/Components/Buttons/Sorting/SortingMatchedCurators'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import {
  toggleCurator,
  selectSelectedCurators,
  setManualMatchingBudget,
  selectManualMatchingBudget,
  filterSelectedCurators,
} from 'src/Redux/selectedCuratorsSlice'
import { setCampaignBudget } from 'src/Redux/spotifyCampaignMatchingProcess'
import { BoxButton } from 'src/Components/Buttons/BoxButton'
import { ManualMatchingCampaignSummaryModal } from 'src/Components/ManualMatchingCampaignSummaryModal'
import { useUpdateManualOrderMetadataMutation } from 'src/Redux/manualMatchingOrderApi'
import { useGetSpotifyPaymentSettingsQuery } from 'src/Redux/artistPaymentSettingsApi'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { ProperGenreOrLanguage } from 'src/Types/tracks'
import { CampaignToSchedule } from 'src/Types/campaigns'
import { ScheduleSteps } from 'src/Types/ui'

import { getIsLanguageActive } from '../helpers/helpers'

import { MATCHED_CURATORS_PAGE_SIZE } from './helper'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 24px 0 0 0;

  @media (min-width: ${MAX_MOBILE_WIDTH}) {
    padding: 24px 24px 0 24px;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const StyledBoxButton = styled(BoxButton)`
  position: absolute;
  bottom: 84px;
  right: 20px;
`

const SKELETON_KEYS = ['sk-0', 'sk-1', 'sk-2', 'sk-3', 'sk-4']

const sortPlaylistsByMatchingPercent = (
  playlists: MatchedCurator['matchedPlaylists'],
): MatchedCurator['matchedPlaylists'] =>
  [...playlists].sort((a, b) => b.matchingPercent - a.matchingPercent)

const getMaxMatchingPercent = (
  playlists: MatchedCurator['matchedPlaylists'],
): number =>
  playlists.length > 0
    ? Math.max(...playlists.map((p) => p.matchingPercent))
    : 0

const renderCurator = (
  curator: MatchedCurator,
  selectedCuratorIds: Set<string>,
  handleToggleCurator: (curator: MatchedCurator) => void,
  favoriteCurators: FavoriteCurator[],
): JSX.Element => {
  const {
    firstName,
    lastName,
    retailPrice,
    matchedPlaylists,
    id: curatorId,
    photoUrl,
    lastActivity,
    madePlacements,
  } = curator

  const isSelected = selectedCuratorIds.has(String(curatorId))
  const isSaved = favoriteCurators.some((f) => f.curatorUserId === curatorId)

  return (
    <CuratorPlaylistsItem
      curatorName={`${firstName} ${lastName}`}
      playlistCount={matchedPlaylists.length}
      price={`$${numberToMoney(retailPrice)}`}
      selected={isSelected}
      playlists={sortPlaylistsByMatchingPercent(matchedPlaylists)}
      key={curatorId}
      avatarUrl={photoUrl}
      onSelect={() => handleToggleCurator(curator)}
      matchingPercent={getMaxMatchingPercent(matchedPlaylists)}
      madePlacements={madePlacements}
      lastActivity={lastActivity}
      isSaved={isSaved}
    />
  )
}

const renderCuratorsList = (
  isLoading: boolean,
  curatorsList: { data: MatchedCurator[]; total: number } | undefined,
  selectedCuratorIds: Set<string>,
  handleToggleCurator: (curator: MatchedCurator) => void,
  favoriteCurators: FavoriteCurator[],
): JSX.Element | JSX.Element[] | null => {
  if (isLoading) {
    return SKELETON_KEYS.map((key) => (
      <PrimarySkeleton key={key} style={{ height: '120px' }} />
    ))
  }

  return (
    curatorsList?.data.map((curator) =>
      renderCurator(
        curator,
        selectedCuratorIds,
        handleToggleCurator,
        favoriteCurators,
      ),
    ) || null
  )
}

const getMatchedSetKey = (
  genres: ProperGenreOrLanguage[],
  languages: ProperGenreOrLanguage[],
): string =>
  `${genres.map((genre) => genre.id).join()}|${languages
    .map((language) => language.id)
    .join()}`

interface CampaignMatchingState {
  matchedSetKey: string
  isSettingsReady: boolean
}

const getCampaignMatchingState = (
  campaignDetails: CampaignToSchedule | null,
): CampaignMatchingState => {
  const genres = campaignDetails?.genres ?? []
  const languages = campaignDetails?.languages ?? []

  return {
    matchedSetKey: getMatchedSetKey(genres, languages),
    isSettingsReady: genres.length > 0 && !getIsLanguageActive(languages),
  }
}

const shouldShowPagination = (
  curatorsList: { total: number } | undefined,
): curatorsList is { total: number } => {
  if (!curatorsList) {
    return false
  }
  return curatorsList.total > MATCHED_CURATORS_PAGE_SIZE
}

export const ManualMatchingCuratorsList: FC = () => {
  const [isCuratorsModalOpen, setIsCuratorsModalOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState(MatchedCuratorsSortBy.BEST_MATCH)
  const params = useParams()
  const id = params.number
  const dispatch = useAppDispatch()
  const { setSearch } = useCustomSearchParams()

  const [updateManualOrderMetadata, { isLoading: isOrderMetadataLoading }] =
    useUpdateManualOrderMetadataMutation()

  const { campaignDetails } = useSpotifyCampaignStatus()
  const { data: favoriteCurators = [] } = useGetFavoriteCuratorsQuery()
  const { isLoading: isMinBudgetLoading } = useGetSpotifyPaymentSettingsQuery(
    void 0,
    { refetchOnMountOrArgChange: true },
  )

  const { matchedSetKey, isSettingsReady } =
    getCampaignMatchingState(campaignDetails)
  const [renderedMatchedSetKey, setRenderedMatchedSetKey] =
    useState(matchedSetKey)

  if (matchedSetKey !== renderedMatchedSetKey) {
    setRenderedMatchedSetKey(matchedSetKey)
    setCurrentPage(1)
  }

  const { data: curatorsList, isFetching } = useGetMatchedCuratorsQuery(
    {
      campaignId: id ?? '',
      pageNumber: currentPage,
      pageSize: MATCHED_CURATORS_PAGE_SIZE,
      sortBy,
    },
    {
      skip: !id || !isSettingsReady,
    },
  )

  const isCuratorsListLoading = isFetching || isMinBudgetLoading

  const handleSortChange = (newSortBy: MatchedCuratorsSortBy): void => {
    isPageChangeRef.current = true
    setSortBy(newSortBy)
    setCurrentPage(1)
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const isPageChangeRef = useRef(false)
  const { scrollToElement } = useContentScroll()

  const handlePageChange = async (page: number): Promise<void> => {
    await scrollToElement(containerRef.current)
    isPageChangeRef.current = true
    setCurrentPage(page)
  }

  const selectedCurators = useAppSelector(selectSelectedCurators)
  const currentManualMatchingBudget = useAppSelector(selectManualMatchingBudget)

  const selectedCuratorIds = useMemo(
    () => new Set(selectedCurators.map((curator) => String(curator.id))),
    [selectedCurators],
  )

  const handleToggleCurator = useCallback(
    (curator: MatchedCurator) => {
      dispatch(toggleCurator(curator))
    },
    [dispatch],
  )

  const prevCuratorIdsRef = useRef(new Set())

  useEffect(() => {
    if (!curatorsList?.data) {
      return
    }

    const currentCuratorIdsSet = new Set(
      curatorsList.data.map((curator) => curator.id),
    )

    if (isPageChangeRef.current) {
      isPageChangeRef.current = false
      prevCuratorIdsRef.current = currentCuratorIdsSet
      return
    }

    const prevSet = prevCuratorIdsRef.current
    const hasChanged =
      prevSet.size !== currentCuratorIdsSet.size ||
      Array.from(currentCuratorIdsSet).some(
        (curatorId) => !prevSet.has(curatorId),
      )

    if (hasChanged || prevSet.size === 0) {
      prevCuratorIdsRef.current = currentCuratorIdsSet

      if (selectedCurators.length > 0) {
        dispatch(filterSelectedCurators(Array.from(currentCuratorIdsSet)))
      }
    }
  }, [dispatch, selectedCurators.length, curatorsList?.data])

  const onOpenSelectedCuratorsModal = (): void => {
    setIsCuratorsModalOpen(true)
  }

  const onContinueClick = async (selectedIds: number[]): Promise<void> => {
    if (!id) {
      return
    }

    try {
      const result = await updateManualOrderMetadata({
        campaignId: id,
        userIds: selectedIds,
      }).unwrap()

      dispatch(filterSelectedCurators(selectedIds))

      if (result.budget !== currentManualMatchingBudget) {
        dispatch(setManualMatchingBudget(result.budget))
      }

      dispatch(setCampaignBudget(result.budget))

      setIsCuratorsModalOpen(false)
      setSearch({ step: ScheduleSteps.CHECKOUT })
    } catch {
      setIsCuratorsModalOpen(false)
    }
  }
  const onCloseClick = (): void => {
    setIsCuratorsModalOpen(false)
  }

  const renderMatchedCurators = (): JSX.Element => (
    <>
      {renderCuratorsList(
        isCuratorsListLoading,
        curatorsList,
        selectedCuratorIds,
        handleToggleCurator,
        favoriteCurators,
      )}
      {shouldShowPagination(curatorsList) && (
        <ListPagination
          current={currentPage}
          total={curatorsList.total}
          pageSize={MATCHED_CURATORS_PAGE_SIZE}
          itemName='curators'
          onChange={handlePageChange}
        />
      )}
      {selectedCurators.length > 0 && (
        <StyledBoxButton
          amount={String(selectedCurators.length)}
          onClick={onOpenSelectedCuratorsModal}
        />
      )}
    </>
  )

  if (!isSettingsReady) {
    return null
  }

  return (
    <Container ref={containerRef}>
      <Header>
        <Title>Choose curators for submission</Title>
        <Sorting
          text={MATCHED_CURATORS_SORT_LABELS[sortBy]}
          renderContent={(onHide) => (
            <SortingMatchedCurators
              value={sortBy}
              onChange={handleSortChange}
              onHide={onHide}
            />
          )}
        />
      </Header>
      {renderMatchedCurators()}
      {isCuratorsModalOpen && (
        <ManualMatchingCampaignSummaryModal
          curators={selectedCurators}
          onContinue={onContinueClick}
          isLoading={isOrderMetadataLoading}
          onClose={onCloseClick}
        />
      )}
    </Container>
  )
}
